/**
 * NETTO+ — racine de l'application et routage.
 *
 * Développé par Mihrem — https://www.mihrem.com
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminLayout } from './components/AdminLayout';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Services } from './components/Services';
import { Benefits } from './components/Benefits';
import { Gallery } from './components/Gallery';
import { Testimonials } from './components/Testimonials';
import { About } from './components/About';
import { QuoteForm } from './components/QuoteForm';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { PriceEstimator } from './components/PriceEstimator';
import { FloatingContact } from './components/FloatingContact';
import { GoogleReviews } from './components/GoogleReviews';
import { getReviews } from './api/api';
import { LegalPage } from './pages/LegalPage';
import { ServicePage } from './pages/ServicePage';
import { AreaPage } from './pages/AreaPage';
import { NotFound } from './pages/NotFound';
import {
  ServicesPage, AProposPage, AvisPage, ContactPage, DevisPage,
} from './pages/PagesSimples';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminServices } from './pages/AdminServices';
import { AdminBenefits } from './pages/AdminBenefits';
import { AdminTestimonials } from './pages/AdminTestimonials';
import { AdminProjects } from './pages/AdminProjects';
import { AdminCompany } from './pages/AdminCompany';
import { AdminAbout } from './pages/AdminAbout';
import { AdminQuotes } from './pages/AdminQuotes';
import { AdminMessages } from './pages/AdminMessages';
import { AdminProfile } from './pages/AdminProfile';
import { AdminAppearance } from './pages/AdminAppearance';
import { AdminAreas } from './pages/AdminAreas';
import { AdminLegal } from './pages/AdminLegal';
import { AdminPricing } from './pages/AdminPricing';
import { AdminPasswordReset } from './pages/AdminPasswordReset';
import { AdminReviews } from './pages/AdminReviews';
import { AdminCourriel } from './pages/AdminCourriel';
import { AdminUsers } from './pages/AdminUsers';
import './App.css';

const HomePage = () => {
  // Étoiles dans les résultats Google : le balisage n'est injecté que si des
  // valeurs réelles ont été saisies dans le panel. Sans données, rien n'est
  // ajouté — un AggregateRating inventé vaut une pénalité manuelle.
  useEffect(() => {
    let balise;
    getReviews()
      .then(({ settings }) => {
        if (!settings?.enabled || !settings.rating || !settings.review_count) return;
        const base = document.querySelector('script[type="application/ld+json"]');
        if (!base) return;

        const donnees = JSON.parse(base.textContent);
        donnees.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: settings.rating,
          reviewCount: settings.review_count,
          bestRating: 5,
          worstRating: 1,
        };
        balise = document.createElement('script');
        balise.type = 'application/ld+json';
        balise.textContent = JSON.stringify(donnees);
        base.remove();
        document.head.appendChild(balise);
      })
      .catch(() => {});

    return () => balise?.remove();
  }, []);

  useEffect(() => {
    const seoTitle = "Entreprise de nettoyage à Liège | NETTO+";
    const seoDescription = "NETTO+, entreprise de nettoyage à Liège. 30 ans de métier, société établie depuis 2016. Bureaux, maisons, vitres et fin de chantier. Devis gratuit sous 24h.";
    const seoKeywords = "entreprise de nettoyage Liège, nettoyage professionnel Liège, société de nettoyage Liège, nettoyage bureaux Liège, nettoyage après chantier Liège";
    
    document.title = seoTitle;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = seoDescription;

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = seoKeywords;
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <Services />
        <Benefits />
        <Gallery />
        <Testimonials />
        <GoogleReviews />
        <About />
        <PriceEstimator />
        <QuoteForm />
        <Contact />
      </main>
      <Footer />
      <FloatingContact />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppearanceProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/a-propos" element={<AProposPage />} />
            <Route path="/avis" element={<AvisPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/devis" element={<DevisPage />} />
            <Route path="/zones/:slug" element={<AreaPage />} />

            {/* Pages légales : chemins explicites, plus lisibles qu'un
                paramètre unique et plus faciles à partager. */}
            <Route path="/mentions-legales" element={<LegalPage />} />
            <Route path="/confidentialite" element={<LegalPage />} />
            <Route path="/cgv" element={<LegalPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reinitialiser" element={<AdminPasswordReset />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="benefits" element={<AdminBenefits />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="company" element={<AdminCompany />} />
              <Route path="about" element={<AdminAbout />} />
              <Route path="appearance" element={<AdminAppearance />} />
              <Route path="quotes" element={<AdminQuotes />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="courriel" element={<AdminCourriel />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="areas" element={<AdminAreas />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AppearanceProvider>
    </AuthProvider>
  );
}

export default App;
